var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { isRectangle } from "./Rectangle.js";
import { nanoid } from "nanoid";
import { positionToCanvasSpace } from "./utils.js";
import { debounce } from "lodash";
import { UIService } from "./UIService.js";
import { InputEventType } from "../inputs/InputManager.js";
import "../global-33d5eebf.js";
import "../utils/playSound.js";
import "../loader/AssetLoader.js";
import "../loader/ImageLoader.js";
import "pixi.js";
import "../loader/SpriteLoader.js";
import "../loader/PixiSpriteLoader.js";
import "../loader/MapLoader.js";
import "../utils/colors.js";
import "../utils/sharedFetch.js";
import "../vendor/noise/index.js";
import "url";
import "@dimforge/rapier2d-compat";
import "../persist/persist.js";
import "../loader/PixiSpineLoader.js";
import "pixi-spine";
import "../loader/SoundLoader.js";
import "../_commonjsHelpers-7a77ea84.js";
import "../console/flags.js";
import "../console/editor.js";
import "../components/ComponentRegistry.js";
import "../decorators/type.js";
import "ajv";
import "../utils/clone.js";
import "bitecs";
import "../utils/typehelpers.js";
import "../enums-86f58524.js";
import "../components/types.js";
import "../utils/byteArray.js";
import "../console/floatingwindow.js";
import "../Description-c36d0b9a.js";
import "../Attach-abbbd93e.js";
import "../utils/vector.js";
import "../utils/windowSessionStorage.js";
const isRootUIElement = (x) => {
  return x._update === void 0;
};
class UIElement {
  constructor(bounds, _config = {}, defaultStyle = {}) {
    __publicField(this, "bounds");
    __publicField(this, "_visible", true);
    __publicField(this, "_id");
    __publicField(this, "_config");
    __publicField(this, "_element");
    __publicField(this, "_parent");
    __publicField(this, "_zIndex", 0);
    __publicField(this, "_creationDate", (/* @__PURE__ */ new Date()).toISOString());
    __publicField(this, "removeTheseChildren", []);
    __publicField(this, "destroyed");
    __publicField(this, "uiService");
    __publicField(this, "cachedStyle");
    __publicField(this, "focusedStyle");
    __publicField(this, "focusedIndices", []);
    __publicField(this, "isVisible", () => {
      var _a;
      const notVisible = this.destroyed || !this._visible || this._config.visible === false || !((_a = this._parent) == null ? void 0 : _a.isVisible());
      const visible = !notVisible;
      return visible;
    });
    __publicField(this, "update", debounce(
      () => {
        if (this.destroyed) {
          return;
        }
        this._update();
      },
      0,
      { leading: false }
    ));
    this.uiService = UIService.getInstance();
    if (!_config.style) {
      _config.style = {};
    }
    this._config = {
      visible: true,
      ..._config,
      style: {
        ...defaultStyle,
        ..._config.style
      }
    };
    if (isRectangle(bounds)) {
      this.bounds = bounds.toPosition();
    } else {
      this.bounds = bounds;
    }
    this._id = nanoid();
    this.uiService.mappedIds[this._id] = this;
    if (this._config.children) {
      for (const child of this._config.children) {
        child.parent = this;
      }
    } else if (this._config.parent) {
      this.parent = this._config.parent;
    }
  }
  get id() {
    return this._id;
  }
  set id(value) {
    delete this.uiService.mappedIds[this._id];
    this._id = value;
    this.element.id = value;
    this.uiService.mappedIds[value] = this;
    this.update();
  }
  get visible() {
    return this._visible;
  }
  set visible(value) {
    this._visible = value;
    this.updateVisibility();
  }
  handleConfigChange(key, value) {
    if (key === "visible") {
      this._config.visible = value;
      this.updateVisibility();
      return;
    }
    if (key === "focusable") {
      this._config.focusable = value;
      if (this._element) {
        if (value) {
          this._element.classList.add("focusable");
        } else {
          this._element.classList.remove("focusable");
        }
      }
      return;
    }
    if (key === "captureFocus") {
      let previousCaptureFocus = this._config.captureFocus;
      this._config.captureFocus = value;
      if (this._element) {
        if (value !== void 0 && value > -1) {
          this._element.classList.add("captureFocus" + value);
          this.uiService.clearFocusedElementByPlayerIndex(value);
          this.update();
        } else {
          if (previousCaptureFocus !== void 0 && previousCaptureFocus > -1) {
            this._element.classList.remove("captureFocus" + previousCaptureFocus);
          }
        }
      }
      return;
    }
    if (key === "autoFocus") {
      this._config.autoFocus = value;
      if (this._element) {
        if (value) {
          this._element.classList.add("autoFocus");
        } else {
          this._element.classList.remove("autoFocus");
        }
      }
      return;
    }
    this._config[key] = value;
    this.update();
  }
  get config() {
    return new Proxy(this._config, {
      set: (target, key, value) => {
        this.handleConfigChange(key, value);
        return true;
      },
      get: (target, key) => {
        if (key === "style") {
          return this.style;
        }
        return target[key];
      }
    });
  }
  get style() {
    return new Proxy(this._config.style, {
      set: (target, key, value) => {
        if (this.cachedStyle) {
          this.cachedStyle[key] = value;
        }
        target[key] = value;
        this.update();
        return true;
      }
    });
  }
  set style(value) {
    this._config.style = value;
    this.update();
  }
  get element() {
    if (!this._element) {
      const element = this.createElement();
      this._element = element;
      this.addEvents(element);
    }
    return this._element;
  }
  set position(value) {
    this.bounds = isRectangle(value) ? value.toPosition() : value;
    this.update();
  }
  get position() {
    return new Proxy(this.bounds, {
      set: (target, key, value) => {
        target[key] = value;
        this.update();
        return true;
      }
    });
  }
  set parent(parent) {
    this._parent = parent;
    if (parent) {
      parent.addChild(this);
    }
  }
  get parent() {
    return this._parent;
  }
  removeAllChildren() {
    var _a;
    this._config.children = [];
    (_a = this._config.children) == null ? void 0 : _a.forEach((x) => {
      x.onDestroy(true);
    });
    this._config.children = [];
    this.update();
  }
  removeChild(child) {
    if (this._config.children) {
      this._config.children = this._config.children.filter((x) => x !== child);
    }
    this.update();
  }
  addChild(child) {
    if (!this._config.children) {
      this._config.children = [];
    }
    if (!this._config.children.includes(child)) {
      this._config.children.push(child);
    }
    if (child._parent !== this) {
      child.parent = this;
    }
    this.update();
  }
  onEscape(playerIndex) {
    if (this._config.onEscape) {
      this._config.onEscape(playerIndex);
    }
  }
  onClick(playerIndex) {
    const focusables = this.uiService.getFocusables(playerIndex);
    if (this._element && (focusables == null ? void 0 : focusables.includes(this._element))) {
      return this.onClickInternal(playerIndex);
    }
  }
  onBlur(playerIndex) {
    return this.onBlurInternal(playerIndex);
  }
  onFocus(playerIndex) {
    return this.onFocusInternal(playerIndex);
  }
  onMouseDown(playerIndex) {
    return this.onMouseDownInternal(playerIndex);
  }
  onMouseUp(playerIndex) {
    return this.onMouseUpInternal(playerIndex);
  }
  onMouseEnter(e, forced = false) {
    var _a, _b;
    const playerIndex = this.uiService.getPlayerEventIndex(InputEventType.MOUSE, 0);
    if (playerIndex === -1) {
      return;
    }
    if (!forced && this.uiService.lastMouseMove + 200 < +/* @__PURE__ */ new Date()) {
      return;
    }
    let mouseInBounds = true;
    if (this._config.focusable) {
      const nestedFocused = (_a = this._element) == null ? void 0 : _a.querySelector(
        `.captureFocus${playerIndex}:not(:has(.captureFocus${playerIndex})):has(.focused)`
      );
      if (nestedFocused) {
        mouseInBounds = false;
      } else {
        const rect = (_b = this._element) == null ? void 0 : _b.getBoundingClientRect();
        if (rect && (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom)) {
          mouseInBounds = false;
        }
      }
      if (!mouseInBounds) {
        return;
      }
    }
    if (this._config.focusable && mouseInBounds) {
      this.uiService.attemptMouseFocus(this);
    }
    return this.onMouseEnterInternal(playerIndex);
  }
  onMouseLeave() {
    const playerIndex = this.uiService.getPlayerEventIndex(InputEventType.MOUSE, 0);
    if (playerIndex === -1) {
      return;
    }
    if (this.uiService.lastMouseMove + 200 < +/* @__PURE__ */ new Date()) {
      return;
    }
    return this.onMouseLeaveInternal(playerIndex);
  }
  onDestroy(noUpdate) {
    var _a, _b;
    this.destroyed = true;
    delete this.uiService.mappedIds[this._id];
    this.removeElement(noUpdate);
    (_a = this == null ? void 0 : this.parent) == null ? void 0 : _a.removeChild(this);
    (_b = this._config.children) == null ? void 0 : _b.forEach((x) => {
      x.onDestroy(true);
    });
  }
  reset() {
  }
  removeElement(noUpdate) {
    var _a, _b, _c, _d;
    if (this._element) {
      (_b = (_a = this._element) == null ? void 0 : _a.parentElement) == null ? void 0 : _b.removeChild(this._element);
      (_c = this._element) == null ? void 0 : _c.remove();
      this._element = void 0;
      if (!noUpdate) {
        const focusIndices = this.uiService.elementFocusIndices(this);
        for (const focusIndex of focusIndices) {
          this.uiService.traverseParentFocusByPlayerIndex(focusIndex);
        }
        if (this.parent && !isRootUIElement(this.parent)) {
          (_d = this.parent) == null ? void 0 : _d.update();
        }
      }
    }
  }
  createElement() {
    const element = document.createElement("div");
    element.id = this.id;
    if (this._config.focusable) {
      element.classList.add("focusable");
    }
    if (this._config.captureFocus !== void 0 && this._config.captureFocus > -1) {
      element.classList.add("captureFocus" + this._config.captureFocus);
      this.uiService.clearFocusedElementByPlayerIndex(this._config.captureFocus);
    }
    if (this._config.autoFocus) {
      element.classList.add("autoFocus");
    }
    return element;
  }
  addEvents(element) {
    element.onclick = (e) => {
      const playerIndex = this.uiService.getPlayerEventIndex(InputEventType.MOUSE, 0);
      if (playerIndex === -1) {
        e.stopPropagation();
        return;
      }
      this.onMouseEnter(e, true);
      const res = this.onClickInternal(playerIndex);
      if (res === false) {
        e.stopPropagation();
      }
    };
    element.onmousedown = (e) => {
      const playerIndex = this.uiService.getPlayerEventIndex(InputEventType.MOUSE, 0);
      if (playerIndex === -1) {
        e.stopPropagation();
        return;
      }
      const res = this.onMouseDownInternal(playerIndex);
      if (res === false) {
        e.stopPropagation();
      }
    };
    element.onmouseup = (e) => {
      const playerIndex = this.uiService.getPlayerEventIndex(InputEventType.MOUSE, 0);
      if (playerIndex === -1) {
        e.stopPropagation();
        return;
      }
      const res = this.onMouseUpInternal(playerIndex);
      if (res === false) {
        e.stopPropagation();
      }
    };
    element.onmouseenter = (e) => {
      this.onMouseEnter(e);
    };
    element.onmouseleave = () => {
      const playerIndex = this.uiService.getPlayerEventIndex(InputEventType.MOUSE, 0);
      if (playerIndex === -1) {
        return;
      }
      this.onMouseLeaveInternal(playerIndex);
    };
  }
  updateVisibility() {
    var _a;
    if (!this.isVisible()) {
      this.focusedStyle = void 0;
      if (this.cachedStyle) {
        this._config.style = this.cachedStyle;
        this.cachedStyle = void 0;
      }
      if (this._element) {
        this.removeElement();
      }
      (_a = this._config.children) == null ? void 0 : _a.forEach((x) => {
        x.updateVisibility();
      });
      return;
    }
    if (!this._element) {
      this.update();
    }
  }
  getScales() {
    let scale = this._config.scale ?? 1;
    let scaleX = this._config.scaleX ?? 1;
    let scaleY = this._config.scaleY ?? 1;
    let parent = this._parent;
    while (parent) {
      scale *= parent.config.scale ?? 1;
      scaleX *= parent.config.scaleX ?? 1;
      scaleY *= parent.config.scaleY ?? 1;
      parent = parent._parent;
    }
    return [scale, scaleX, scaleY];
  }
  _update() {
    var _a, _b, _c, _d, _e;
    if (!this.isVisible()) {
      this.updateVisibility();
      return;
    }
    this._zIndex = (((_a = this._parent) == null ? void 0 : _a._zIndex) ?? 0) + parseInt(this._config.style.zIndex ?? "0");
    const element = this.element;
    if (!element) {
      return;
    }
    element.style.display = "block";
    const focusIndices = this.uiService.elementFocusIndices(this);
    const newFocusedIndices = focusIndices.filter((x2) => !this.focusedIndices.includes(x2));
    const removedFocusedIndices = this.focusedIndices.filter((x2) => !focusIndices.includes(x2));
    this.focusedIndices = focusIndices;
    if (newFocusedIndices.length > 0) {
      if (!this.focusedStyle) {
        this.focusedStyle = {
          ...this._config.style,
          ...this._config.focusStyle
        };
        const reset = Object.entries(this.focusedStyle).reduce((acc, [key, value]) => {
          acc[key] = "";
          return acc;
        }, {});
        this.cachedStyle = {
          ...reset,
          ...this._config.style
        };
        this._config.style = this.focusedStyle;
        this._element.focus();
        this._element.classList.add("focused");
        setTimeout(() => {
          const focusIndex = this.uiService.elementFocusIndices(this);
          for (const index of newFocusedIndices) {
            if (focusIndex.includes(index)) {
              this.onFocus(index);
            }
          }
        }, 0);
      }
    }
    if (removedFocusedIndices.length && this.cachedStyle) {
      if (focusIndices.length === 0) {
        this._config.style = this.cachedStyle;
        this.cachedStyle = void 0;
        this.focusedStyle = void 0;
        this._element.classList.remove("focused");
      }
      for (const index of removedFocusedIndices) {
        this.onBlur(index);
      }
    }
    const parentElement = (_b = this.parent) == null ? void 0 : _b._element;
    if (!element.parentElement || parentElement && element.parentElement !== parentElement) {
      parentElement == null ? void 0 : parentElement.appendChild(element);
    }
    if (parentElement) {
      element.style.order = `${((_c = this.parent.config.children) == null ? void 0 : _c.indexOf(this)) ?? 0}`;
    }
    const styles = {
      ...this._config.style
    };
    const [x, y, width, height] = positionToCanvasSpace(
      this.bounds,
      ((_d = this._parent) == null ? void 0 : _d._element) ?? document.body,
      ...this.getScales()
    );
    if (styles.position === "absolute") {
      element.style.left = `${x}px`;
      element.style.top = `${y}px`;
    }
    element.style.width = width > 1 ? `${width}px` : "auto";
    element.style.height = height > 1 ? `${height}px` : "auto";
    for (const [key, value] of Object.entries(styles)) {
      element.style[key] = value;
    }
    if (this._zIndex > 0) {
      element.style.zIndex = `${this._zIndex}`;
    }
    (_e = this.config.children) == null ? void 0 : _e.forEach((x2, index) => {
      x2.update();
      if (x2._element) {
        x2._element.style.order = `${index}`;
      }
    });
  }
}
export {
  UIElement
};
//# sourceMappingURL=UIElement.js.map
